import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import './Backup.css';

interface FileInfo {
  key: string;
  fileName: string;
  size: number;
  sizeFormatted: string;
  lastModified: string;
  category: string;
  subCategory?: string;
}

interface FilesData {
  whatsappBackups: FileInfo[];
  whatsappMedia: FileInfo[];
  whatsappWebhook: FileInfo[];
  logos: FileInfo[];
  uploads: FileInfo[];
  total: number;
  totalSize: number;
  totalSizeFormatted: string;
}

interface StorageStats {
  whatsappBackup: { count: number; sizeFormatted: string };
  whatsappMedia: { count: number; sizeFormatted: string };
  whatsappWebhook: { count: number; sizeFormatted: string };
  logos: { count: number; sizeFormatted: string };
  uploads: { count: number; sizeFormatted: string };
  total: { count: number; sizeFormatted: string };
}

const Backup: React.FC = () => {
  const [files, setFiles] = useState<FilesData | null>(null);
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [filesResponse, statsResponse] = await Promise.all([
        apiService.getCustom<{ success: boolean; data: FilesData }>('/backup/files'),
        apiService.getCustom<{ success: boolean; data: StorageStats }>('/backup/storage-stats')
      ]);

      setFiles(filesResponse.data);
      setStats(statsResponse.data);
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      alert(error.response?.data?.message || 'Erro ao carregar dados de backup');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileKey: string, fileName: string) => {
    try {
      setDownloadingFile(fileKey);

      const response = await apiService.postCustom<{ success: boolean; data: { downloadUrl: string } }>('/backup/download', { fileKey });
      const downloadUrl = response.data.downloadUrl;

      // Abrir URL em nova aba para download
      window.open(downloadUrl, '_blank');

      console.log(`Download de ${fileName} iniciado!`);
    } catch (error: any) {
      console.error('Erro ao gerar URL de download:', error);
      alert(error.response?.data?.message || 'Erro ao iniciar download');
    } finally {
      setDownloadingFile(null);
    }
  };

  const renderFileList = (fileList: FileInfo[], title: string) => {
    if (!fileList || fileList.length === 0) {
      return (
        <div className="no-files">
          <i className="fas fa-folder-open"></i>
          <p>Nenhum arquivo encontrado</p>
        </div>
      );
    }

    const isLogosSection = title === 'Logomarcas';

    return (
      <div className="file-list">
        <h3>{title} ({fileList.length})</h3>
        <div className="table-responsive">
          <table className="files-table">
            <thead>
              <tr>
                <th>Arquivo</th>
                {isLogosSection && <th>Tipo</th>}
                <th>Tamanho</th>
                <th>Última Modificação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {fileList.map((file) => (
                <tr key={file.key}>
                  <td>
                    <div className="file-name">
                      <i className={`fas ${getFileIcon(file.fileName)}`}></i>
                      <span>{file.fileName}</span>
                    </div>
                  </td>
                  {isLogosSection && (
                    <td>
                      <span className={`file-type-badge ${file.category}`}>
                        {file.category === 'logo-empresa' ? 'Logo da Empresa' : 'Foto de Perfil'}
                      </span>
                    </td>
                  )}
                  <td>{file.sizeFormatted}</td>
                  <td>{new Date(file.lastModified).toLocaleString('pt-BR')}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleDownload(file.key, file.fileName)}
                      disabled={downloadingFile === file.key}
                    >
                      {downloadingFile === file.key ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Baixando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-download"></i> Baixar
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'zip':
      case 'gz':
      case 'tar':
        return 'fa-file-archive';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fa-file-image';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'fa-file-video';
      case 'mp3':
      case 'wav':
      case 'ogg':
        return 'fa-file-audio';
      default:
        return 'fa-file';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="backup-page">
      <div className="page-header">
        <h1>
          <i className="fas fa-database"></i> Backup e Downloads
        </h1>
        <p>Gerencie e faça download dos seus arquivos armazenados</p>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon whatsapp">
              <i className="fab fa-whatsapp"></i>
            </div>
            <div className="stat-info">
              <h4>Backups WhatsApp</h4>
              <p className="stat-value">{stats.whatsappBackup.count}</p>
              <p className="stat-size">{stats.whatsappBackup.sizeFormatted}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon media">
              <i className="fas fa-photo-video"></i>
            </div>
            <div className="stat-info">
              <h4>Mídias WhatsApp</h4>
              <p className="stat-value">{stats.whatsappMedia.count}</p>
              <p className="stat-size">{stats.whatsappMedia.sizeFormatted}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon logos">
              <i className="fas fa-image"></i>
            </div>
            <div className="stat-info">
              <h4>Logo/Perfil</h4>
              <p className="stat-value">{stats.logos.count}</p>
              <p className="stat-size">{stats.logos.sizeFormatted}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon uploads">
              <i className="fas fa-file-upload"></i>
            </div>
            <div className="stat-info">
              <h4>Uploads</h4>
              <p className="stat-value">{stats.uploads.count}</p>
              <p className="stat-size">{stats.uploads.sizeFormatted}</p>
            </div>
          </div>

          <div className="stat-card total">
            <div className="stat-icon">
              <i className="fas fa-database"></i>
            </div>
            <div className="stat-info">
              <h4>Total</h4>
              <p className="stat-value">{stats.total.count} arquivos</p>
              <p className="stat-size">{stats.total.sizeFormatted}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <i className="fas fa-th-large"></i> Todos os Arquivos
        </button>
        <button
          className={`tab ${activeTab === 'whatsapp' ? 'active' : ''}`}
          onClick={() => setActiveTab('whatsapp')}
        >
          <i className="fab fa-whatsapp"></i> Backups WhatsApp
        </button>
        <button
          className={`tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          <i className="fas fa-photo-video"></i> Mídias
        </button>
        <button
          className={`tab ${activeTab === 'logos' ? 'active' : ''}`}
          onClick={() => setActiveTab('logos')}
        >
          <i className="fas fa-image"></i> Logomarcas
        </button>
        <button
          className={`tab ${activeTab === 'uploads' ? 'active' : ''}`}
          onClick={() => setActiveTab('uploads')}
        >
          <i className="fas fa-file-upload"></i> Uploads
        </button>
      </div>

      {/* Conteúdo das Tabs */}
      <div className="tab-content">
        {activeTab === 'all' && files && (
          <>
            {renderFileList(files.whatsappBackups, 'Backups WhatsApp')}
            {renderFileList(files.whatsappMedia, 'Mídias WhatsApp')}
            {renderFileList(files.logos, 'Logomarcas')}
            {renderFileList(files.uploads, 'Uploads')}
          </>
        )}

        {activeTab === 'whatsapp' && files && renderFileList(files.whatsappBackups, 'Backups WhatsApp')}
        {activeTab === 'media' && files && renderFileList(files.whatsappMedia, 'Mídias WhatsApp')}
        {activeTab === 'logos' && files && renderFileList(files.logos, 'Logomarcas')}
        {activeTab === 'uploads' && files && renderFileList(files.uploads, 'Uploads')}
      </div>

      {/* Botão de Atualizar */}
      <div className="actions">
        <button className="btn btn-secondary" onClick={loadData} disabled={loading}>
          <i className="fas fa-sync-alt"></i> Atualizar Lista
        </button>
      </div>
    </div>
  );
};

export default Backup;
